import React from "react";
import {
  Plane,
  Star,
  BedDouble,
  Bath,
  Home as HomeIcon,
  Car,
  Fuel,
  Gauge,
  Cog,
  Anchor,
  Ruler,
  Waves,
  Calendar,
  Clock,
  ArrowRight,
  CheckCircle2,
  Cpu,
  ShieldCheck,
  Palette,
  Zap,
  Shirt,
  Package as PackageIcon,
  Droplets,
  Sparkles as SparklesIcon,
  User,
} from "lucide-react";
import { formatAttrValue, formatMoney } from "../lib/categories";

/**
 * Category-specific detail sub-layouts for the Product page.
 * Falls back to a generic attribute grid for categories without a bespoke layout.
 */
export default function CategorySpecs({ product }) {
  const a = product.attributes || {};

  switch (product.category) {
    case "flights":
      return <FlightItinerary attrs={a} price={product.price} />;
    case "hotels":
      return <HotelSpecs attrs={a} price={product.price} />;
    case "cars":
      return <CarSpecs attrs={a} />;
    case "houses":
      return <HouseSpecs attrs={a} />;
    case "boats":
      return <BoatSpecs attrs={a} />;
    case "electronics":
      return <ElectronicsSpecs attrs={a} />;
    case "clothing":
      return <ClothingSpecs attrs={a} />;
    case "beauty":
      return <BeautySpecs attrs={a} />;
    default:
      return <GenericAttrs attrs={a} category={product.category} />;
  }
}

/* ------- FLIGHTS ------- */
function FlightItinerary({ attrs, price }) {
  const {
    origin,
    destination,
    departureDate,
    cabinClass,
    durationHours,
    airline,
  } = attrs;
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-flight"
    >
      <div className="flex items-center gap-2 mb-4">
        <Plane size={16} strokeWidth={1.5} className="text-neutral-700" />
        <span className="text-[11px] uppercase tracking-widest text-neutral-500">
          Itinerary · {airline}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            From
          </div>
          <div className="font-display font-semibold text-lg text-neutral-900 mt-1 leading-tight">
            {origin}
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full flex items-center gap-2 text-neutral-400">
            <div className="flex-1 h-px bg-neutral-300" />
            <Plane size={16} strokeWidth={1.5} />
            <div className="flex-1 h-px bg-neutral-300" />
          </div>
          <div className="text-[11px] text-neutral-500 mt-1 inline-flex items-center gap-1">
            <Clock size={11} /> {durationHours}h non-stop
          </div>
        </div>
        <div className="flex-1 text-right">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">
            To
          </div>
          <div className="font-display font-semibold text-lg text-neutral-900 mt-1 leading-tight">
            {destination}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-neutral-200">
        <SpecCell icon={<Calendar size={14} />} label="Depart" value={departureDate} />
        <SpecCell icon={<Star size={14} />} label="Cabin" value={cabinClass} />
        <SpecCell icon={<ArrowRight size={14} />} label="Fare" value={formatMoney(price)} />
      </div>
    </div>
  );
}

/* ------- HOTELS ------- */
function HotelSpecs({ attrs, price }) {
  const {
    location,
    starRating,
    pricePerNight,
    nights,
    amenities = [],
    brand,
  } = attrs;
  return (
    <div className="mt-6 space-y-4" data-testid="specs-hotel">
      <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <HomeIcon size={16} strokeWidth={1.5} className="text-neutral-700" />
          <span className="text-[11px] uppercase tracking-widest text-neutral-500">
            Property · {brand}
          </span>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-0.5">
            {Array.from({ length: starRating || 0 }).map((_, i) => (
              <Star
                key={i}
                size={16}
                strokeWidth={1.5}
                className="text-neutral-900 fill-neutral-900"
              />
            ))}
          </span>
          <span className="text-neutral-600 text-sm">{location}</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-neutral-200">
          <SpecCell label="Per night" value={`$${Number(pricePerNight).toLocaleString()}`} />
          <SpecCell label="Nights" value={nights} />
          <SpecCell label="Total" value={formatMoney(price)} />
        </div>
      </div>
      {amenities.length > 0 && (
        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
            Amenities
          </div>
          <ul className="grid grid-cols-2 gap-y-2 gap-x-3">
            {amenities.map((a) => (
              <li
                key={a}
                className="inline-flex items-center gap-2 text-sm text-neutral-800"
              >
                <CheckCircle2 size={14} className="text-green-600 shrink-0" />
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------- CARS ------- */
function CarSpecs({ attrs }) {
  const cells = [
    { icon: <Calendar size={14} />, label: "Year", value: attrs.year },
    { icon: <Gauge size={14} />, label: "Mileage", value: attrs.mileage ? `${Number(attrs.mileage).toLocaleString()} mi` : "—" },
    { icon: <Car size={14} />, label: "Body", value: attrs.bodyType },
    { icon: <Fuel size={14} />, label: "Fuel", value: attrs.fuel },
    { icon: <Cog size={14} />, label: "Transmission", value: attrs.transmission },
    { label: "Make", value: attrs.make },
    { label: "Model", value: attrs.model },
  ];
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-car"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        Vehicle specifications
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
        {cells.filter((c) => c.value !== undefined).map((c) => (
          <SpecCell key={c.label} icon={c.icon} label={c.label} value={c.value} />
        ))}
      </div>
    </div>
  );
}

/* ------- HOUSES ------- */
function HouseSpecs({ attrs }) {
  const cells = [
    { icon: <BedDouble size={14} />, label: "Bedrooms", value: attrs.bedrooms },
    { icon: <Bath size={14} />, label: "Bathrooms", value: attrs.bathrooms },
    { icon: <Ruler size={14} />, label: "Sqft", value: attrs.sqft ? `${Number(attrs.sqft).toLocaleString()}` : "—" },
    { icon: <Calendar size={14} />, label: "Built", value: attrs.yearBuilt },
    { icon: <HomeIcon size={14} />, label: "Style", value: attrs.style },
    { label: "Neighborhood", value: attrs.city },
  ];
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-house"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        Property details
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
        {cells.filter((c) => c.value !== undefined).map((c) => (
          <SpecCell key={c.label} icon={c.icon} label={c.label} value={c.value} />
        ))}
      </div>
    </div>
  );
}

/* ------- BOATS ------- */
function BoatSpecs({ attrs }) {
  const cells = [
    { icon: <Ruler size={14} />, label: "Length", value: attrs.lengthFt ? `${attrs.lengthFt} ft` : "—" },
    { icon: <Anchor size={14} />, label: "Type", value: attrs.boatType },
    { icon: <Waves size={14} />, label: "Hull", value: attrs.hullMaterial },
    { icon: <Calendar size={14} />, label: "Year", value: attrs.year },
    { label: "Builder", value: attrs.builder },
  ];
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-boat"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        Vessel specifications
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
        {cells.filter((c) => c.value !== undefined).map((c) => (
          <SpecCell key={c.label} icon={c.icon} label={c.label} value={c.value} />
        ))}
      </div>
    </div>
  );
}

/* ------- ELECTRONICS ------- */
function ElectronicsSpecs({ attrs }) {
  const cells = [
    { icon: <Cpu size={14} />, label: "Device", value: attrs.deviceType },
    { icon: <Zap size={14} />, label: "Brand", value: attrs.brand },
    { icon: <ShieldCheck size={14} />, label: "Warranty", value: attrs.warrantyYears ? `${attrs.warrantyYears} yrs` : "—" },
    { icon: <Palette size={14} />, label: "Color", value: attrs.color },
  ];
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-electronics"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3 inline-flex items-center gap-1.5">
        <Cpu size={12} /> Tech specs
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-4">
        {cells.filter((c) => c.value !== undefined).map((c) => (
          <SpecCell key={c.label} icon={c.icon} label={c.label} value={c.value} />
        ))}
      </div>
      <div className="mt-5 pt-5 border-t border-neutral-200 grid grid-cols-3 gap-3">
        <BadgeRow icon={<ShieldCheck size={12} />} text="Manufacturer certified" />
        <BadgeRow icon={<Zap size={12} />} text="Fast dispatch" />
        <BadgeRow icon={<Cpu size={12} />} text="Fictional firmware v1.2" />
      </div>
    </div>
  );
}

/* ------- CLOTHING ------- */
function ClothingSpecs({ attrs }) {
  const {
    brand, garmentType, size, gender, color, material,
  } = attrs;
  const sizeOrder = ["XS", "S", "M", "L", "XL", "XXL"];
  const isStandardSize = sizeOrder.includes(size);
  return (
    <div className="mt-6 space-y-4" data-testid="specs-clothing">
      <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3 inline-flex items-center gap-1.5">
          <Shirt size={12} /> Fit & style
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
          <SpecCell icon={<Shirt size={14} />} label="Garment" value={garmentType} />
          <SpecCell icon={<User size={14} />} label="Gender" value={gender} />
          <SpecCell icon={<Palette size={14} />} label="Color" value={color} />
          <SpecCell icon={<PackageIcon size={14} />} label="Material" value={material} />
          <SpecCell label="Brand" value={brand} />
        </div>
      </div>
      {isStandardSize && (
        <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
            Size
          </div>
          <div className="flex flex-wrap gap-2">
            {sizeOrder.map((s) => (
              <span
                key={s}
                className={`inline-flex items-center justify-center h-9 min-w-[44px] px-3 rounded-full border text-sm font-medium ${
                  s === size
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "border-neutral-300 text-neutral-500"
                }`}
                data-testid={`size-pill-${s.toLowerCase()}`}
              >
                {s}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-neutral-500 mt-3">
            Fits true to size. Machine wash cold, tumble dry low.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------- BEAUTY ------- */
function BeautySpecs({ attrs }) {
  const { brand, category, subtype, volumeMl, skinType } = attrs;
  return (
    <div className="mt-6 space-y-4" data-testid="specs-beauty">
      <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <SparklesIcon size={16} strokeWidth={1.5} className="text-neutral-700" />
          <span className="text-[11px] uppercase tracking-widest text-neutral-500">
            Formulation · {brand}
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="font-display font-bold text-3xl text-neutral-900">
            {volumeMl ? `${volumeMl}ml` : "—"}
          </span>
          <span className="text-sm text-neutral-500">{category} · {subtype}</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-neutral-200">
          <SpecCell icon={<Droplets size={14} />} label="Volume" value={volumeMl ? `${volumeMl} ml` : "—"} />
          <SpecCell icon={<SparklesIcon size={14} />} label="Category" value={category} />
          <SpecCell icon={<User size={14} />} label="Skin type" value={skinType} />
        </div>
      </div>
      <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
          Highlights
        </div>
        <ul className="grid grid-cols-2 gap-y-2 gap-x-3">
          {[
            "Cruelty-free formulation",
            "Dermatologist-reviewed (fictionally)",
            "Free of parabens & sulfates",
            "Recyclable packaging",
          ].map((h) => (
            <li key={h} className="inline-flex items-center gap-2 text-sm text-neutral-800">
              <CheckCircle2 size={14} className="text-green-600 shrink-0" />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BadgeRow({ icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600">
      {icon} {text}
    </span>
  );
}

/* ------- GENERIC ------- */
function GenericAttrs({ attrs }) {
  const keys = Object.keys(attrs || {});
  if (keys.length === 0) return null;
  return (
    <div
      className="mt-6 border border-neutral-200 rounded-2xl p-5 bg-white"
      data-testid="specs-generic"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        Product details
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
        {keys.map((k) => (
          <SpecCell key={k} label={humanize(k)} value={formatAttrValue(k, attrs[k])} />
        ))}
      </div>
    </div>
  );
}

/* ------- SHARED ------- */
function SpecCell({ icon, label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="mt-1 text-sm text-neutral-900 font-medium leading-snug">
        {value === undefined || value === null || value === "" ? "—" : value}
      </div>
    </div>
  );
}

function humanize(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}
